import _ from "lodash";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useNavigate, useParams } from "react-router-dom";
import { Formik, FormikErrors, FormikValues } from "formik";

import Navbar from "../../../../common/Navbar/Navbar";
import Layout from "../../../../common/layouts/Layout";
import CreateIRBSubmissionForm from "./CreateIRBSubmissionForm";

import { IRB_SUBMISSION_TYPES, RoutingList } from "../../../../../constants";
import {
  useCreateIrbSubmissionMutation,
  useUpdateIrbSubmissionMutation,
} from "../../../../../generated/graphql";
import { logger } from "../../../../../utils/logger";
import { IRBSubmissionValidationSchema, createIRBInitData } from "../utils";

import useAppStore from "../../../../../states/zustand/appStore";
import { DocumentSubmissionDropdown } from "../../../../../types/forms/DoumentSubmission";

const irbSteps = ["1. Study Details", "2. MIRB Submission"];

const CreateIRBSubmissionContainer = () => {
  const [addIRBSubmission] = useCreateIrbSubmissionMutation();
  const { setLoader } = useAppStore();
  const [updateIrbSubmissionMutation] = useUpdateIrbSubmissionMutation();

  const navigate = useNavigate();
  const { id } = useParams();

  const [steps, setSteps] = useState<number>(0);
  const [stepsInfo, setStepInfo] = useState(irbSteps);

  useEffect(() => {
    if (id) {
      setStepInfo([irbSteps[0]]);
    }
  }, [id]);

  const stepHandler = (
    validateForm: (values: FormikValues) => Promise<any>,
    values: FormikValues,
    stepIndex: number,
    setErrors?: (errors: FormikErrors<FormikValues>) => void
  ) => {
    validateForm(values).then((res) => {
      if (Object.entries(res).length === 0) {
        // validation success
        const lastStepIndex = stepsInfo?.length - 1;
        if (stepIndex <= -1) {
          setSteps(0);
          return;
        } else if (steps === lastStepIndex && stepIndex > lastStepIndex) {
          saveHandler(values);
          return;
        } else if (stepIndex <= lastStepIndex) {
          setSteps((prev) => stepIndex);
          return;
        }
      } else {
        // validation failed
        logger.error(res);
        if (setErrors) {
          setErrors(res);
        }
      }
    });
  };

  const saveHandler = async (values: FormikValues) => {
    try {
      setLoader(true);
      if (id) {
        await updateIrbSubmissionMutation({
          variables: {
            id: id,
            department: values.department,
            type: IRB_SUBMISSION_TYPES.INITIAL_SUBMISSION,
            micr: values.micr,
            dateOfIRBSubmission: values.dateOfIRBSubmission,
            emails: values.emails,
            fundType: values.fundType,
            isFunded: values.isFunded,
            principalInvestigator: values.principalInvestigator,
            protocolNumber: values.protocolNumber,
            sponsor: values.sponsor,
            studyPhase: values.studyPhase,
            title: values.title,
            typeOfStudy: values.typeOfStudy,
          },
        });
        toast.success("MIRB submission updated successfully!");
      } else {
        await addIRBSubmission({
          variables: {
            type: IRB_SUBMISSION_TYPES.INITIAL_SUBMISSION,
            micr: values.micr,
            IRBPresentation: values.IRBPresentation,
            dateOfIRBSubmission: values.dateOfIRBSubmission,
            department: values.department,
            documents: values.documents
              .filter((doc: DocumentSubmissionDropdown) => doc.url !== "")
              .map((doc: DocumentSubmissionDropdown ) => ({
                label: doc.dropdownSelectedValue ? doc.label + '(' + doc.dropdownSelectedValue + ')' : doc.label,
                url: doc.url,
                remark: doc.remark,
                // dropdownSelectedValue: doc.dropdownSelectedValue|| ''
              })),
            emails: values.emails|| [],
            fundType: values.fundType,
            isFunded: values.isFunded || false,
            principalInvestigator: values.principalInvestigator,
            protocolNumber: values.protocolNumber,
            sponsor: values.sponsor,
            studyPhase: values.studyPhase,
            title: values.title,
            typeOfStudy: values.typeOfStudy,
          },
        });
        toast.success("MIRB submission created successfully!");
      }
    } catch (err: any) {
      toast.error(
        (err as { message: string })?.message || "Something went wrong"
      );
      logger.error(err);
    } finally {
      setLoader(false);
      navigate(RoutingList.irb.index.path);
    }
  };

  return (
    <Formik
      initialValues={_.cloneDeep(createIRBInitData)}
      validationSchema={IRBSubmissionValidationSchema[steps]}
      onSubmit={(values, { setSubmitting, setErrors, validateForm }) => {
        stepHandler(
          validateForm,
          values,
          steps > stepsInfo?.length - 1 ? steps : steps + 1,
          setErrors
        );
      }}
    >
      <Layout>
        <>
          <Navbar pageTitle="New MIRB Submissions" />
          <CreateIRBSubmissionForm
            steps={steps}
            setSteps={setSteps}
            stepsInfo={stepsInfo}
          />
        </>
      </Layout>
    </Formik>
  );
};

export default CreateIRBSubmissionContainer;
